/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMutation } from "@apollo/client";
import { showNotification, updateNotification } from "@mantine/notifications";
import { FC, Fragment, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-location";
import { useAppDispatch } from "../../app/hooks";
import { authenticate } from "../../features/users";
import {
  GenerateOtp,
  GenerateOtpVariables,
} from "../../graphql/mutations/__generated__/GenerateOtp";
import {
  Login,
  LoginVariables,
  Login_login,
} from "../../graphql/mutations/__generated__/Login";
import {
  ValidateOtp,
  ValidateOtpVariables,
} from "../../graphql/mutations/__generated__/ValidateOtp";
import { getOTP, userLogin, validateOTP } from "../../graphql/mutations/auth";
import { usePage } from "../../hooks/usePage";

interface LoginScreenProps {}

const LoginScreen: FC<LoginScreenProps> = () => {
  usePage("Kava | Login");
  const navigate = useNavigate();
  const [step, setstep] = useState(0);
  const [otp, setOtp] = useState("");
  const dispatch = useAppDispatch();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<LoginVariables & { otp: string }>();

  const [login, { loading }] = useMutation<Login, LoginVariables>(userLogin);
  const [generateOtp, { loading: generating }] = useMutation<
    GenerateOtp,
    GenerateOtpVariables
  >(getOTP);
  const [validate, { loading: validating }] = useMutation<
    ValidateOtp,
    ValidateOtpVariables
  >(validateOTP);

  const onSubmit: SubmitHandler<LoginVariables & { otp: string }> = (data) => {
    if (step === 0) {
      showNotification({
        id: "login",
        loading: true,
        title: "Getting OTP",
        message: "Sending OTP to your email...",
        autoClose: false,
        disallowClose: true,
      });
      generateOtp({
        variables: {
          args: {
            identifier: data?.input?.username || "",
          },
        },
      })
        .then((res) => {
          if (res.data?.generateOtp?.success) {
            const message = res.data?.generateOtp?.message || "";

            if (message.includes("OTP disabled by user")) {
              setstep(2);
              updateNotification({
                id: "login",
                title: "Success",
                message: "Please enter your password to continue",
                color: "blue",
              });
            } else {
              setstep(1);
              setOtp(res.data?.generateOtp?.otp || "");
              updateNotification({
                id: "login",
                title: "Success",
                message: "OTP sent to your email",
                color: "blue",
              });
            }
          } else {
            updateNotification({
              id: "login",
              title: "Failed",
              message: res.data?.generateOtp?.message || "Failed to send OTP",
              color: "red",
            });
          }
        })
        .catch((error) => {
          updateNotification({
            id: "login",
            title: "Error",
            message: "The email address was not found",
            color: "red",
          });
        });

      return;
    }

    if (step === 1) {
      showNotification({
        id: "login",
        loading: true,
        title: "Validating OTP",
        message: "Verifying your OTP...",
        autoClose: false,
        disallowClose: true,
      });
      validate({
        variables: {
          args: {
            identifier: data?.input?.username || "",
            otp: data.otp,
          },
        },
      })
        .then((res) => {
          if (res?.data?.validateOtp?.success) {
            updateNotification({
              id: "login",
              title: "Success",
              message: res.data?.validateOtp?.message || "OTP is correct",
              color: "blue",
            });
            setstep(2);
          } else {
            updateNotification({
              id: "login",
              title: "Error",
              message:
                res.data?.validateOtp?.message || "The OTP was incorrect",
              color: "red",
            });
          }
        })
        .catch((err) => {
          updateNotification({
            id: "login",
            title: "Error",
            message: "The OTP was incorrect",
            color: "red",
          });
        });
      return;
    }

    showNotification({
      id: "login",
      loading: true,
      title: "Logging in",
      message: "Authenticating your credentials...",
      autoClose: false,
      disallowClose: true,
    });
    login({ variables: data })
      .then((res) => {
        dispatch(authenticate(res.data?.login as Login_login));
        navigate({
          to: "/app/dashboard",
          replace: true,
        });
        updateNotification({
          id: "login",
          title: "Success",
          message: "Login Successful",
          color: "blue",
        });
      })
      .catch(() => {
        updateNotification({
          id: "login",
          title: "Error",
          message: "The user credentials were incorrect",
          color: "red",
        });
      });
  };

  return (
    <Fragment>
      <div className="flex flex-col items-center">
        <h2 className="mt-6 text-3xl font-medium text-gray-900">
          Sign in to your account
        </h2>
      </div>
      <div className="mt-8 max-w-sm w-full">
        <div className="mt-6">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <div className="flex justify-between">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
              </div>
              <div className="mt-1">
                <input
                  type="email"
                  {...register("input.username", { required: "Required" })}
                  id="email"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="you@example.com"
                  disabled={step > 0}
                  aria-describedby="email-optional"
                />
                {errors?.input?.username && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors?.input?.username?.message}
                  </p>
                )}
              </div>
            </div>

            {step === 1 && (
              <div>
                <div className="flex justify-between">
                  <label
                    htmlFor="otp"
                    className="block text-sm font-medium text-gray-700"
                  >
                    OTP
                  </label>
                </div>
                <div className="mt-1">
                  <input
                    type="text"
                    {...register("otp", { required: "Required" })}
                    id="otp"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                    placeholder="Enter OTP sent to your email"
                  />
                  {errors?.otp && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors?.otp?.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="flex justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                </div>
                <div className="mt-1">
                  <input
                    type="password"
                    {...register("input.password", { required: "Required" })}
                    id="password"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                    placeholder="***********"
                  />
                  {errors?.input?.password && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors?.input?.password?.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="text-sm">
                <a
                  onClick={() => navigate({ to: "/forgot-password" })}
                  className="font-medium text-green-600 cursor-pointer"
                >
                  Forgot your password?
                </a>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || generating || validating}
                className="w-full flex justify-center py-2 px-4 border bg-white rounded-md shadow-sm text-sm font-medium text-green-600 hover:text-white border-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 transition duration-200"
              >
                {step === 0
                  ? "Authenticate"
                  : step === 1
                  ? "Verify OTP"
                  : "Login"}
                {(loading || generating || validating) && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default LoginScreen;
